import { useEffect, useMemo, useRef } from 'react'
import { useRecipeBreakdown } from '../hooks/useRecipeBreakdown.js'
import { formatMoney, formatQty } from '../lib/format.js'
import KitchenLoader from './KitchenLoader.jsx'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled])'

// Bottom-sheet modal showing the component-level breakdown for a menu
// kill candidate. Mirrors SuggestReorderModal's frame + animations.
//
// Lower section shows the AI brief's advisory rationale for this item
// when the user has already generated a brief — null otherwise.
export default function MenuKillDetailModal({
  item,
  briefRationale,
  isOpen,
  onClose,
}) {
  const itemId = isOpen ? (item?.item_id || null) : null
  const {
    components, totalCost, salePrice,
    marginPct, targetFoodCostPct, loading, error,
  } = useRecipeBreakdown(itemId)

  // Refs MUST live above the early return — React hook ordering rule
  // (CLAUDE.md, PromotionsScreen #310 incident 2026-05-10).
  const panelRef = useRef(null)
  const closeBtnRef = useRef(null)
  const previouslyFocusedRef = useRef(null)

  // Esc to close + focus management. Capture phase so an outer modal
  // (e.g. AskAiModal) doesn't also handle the same ESC press — we
  // stop propagation after invoking onClose.
  useEffect(() => {
    if (!isOpen) return

    previouslyFocusedRef.current =
      typeof document !== 'undefined' ? document.activeElement : null

    // Move focus to the modal's Close button after paint so the
    // sheet-up animation isn't visible-but-unfocused. rAF is plenty
    // here — the panel mounts synchronously.
    const raf =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame(() => {
            closeBtnRef.current?.focus?.()
          })
        : null

    function onKey(e) {
      if (e.defaultPrevented) return
      if (e.key === 'Escape') {
        onClose?.()
        e.stopPropagation()
        e.preventDefault()
      }
    }
    window.addEventListener('keydown', onKey, true)

    return () => {
      window.removeEventListener('keydown', onKey, true)
      if (raf != null && typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(raf)
      }
      // Restore focus to whatever opened the modal so screen readers
      // and keyboard users land back on the row that was clicked.
      const prev = previouslyFocusedRef.current
      if (prev && typeof prev.focus === 'function' && document.body.contains(prev)) {
        prev.focus()
      } else if (typeof document !== 'undefined') {
        document.body?.focus?.()
      }
    }
  }, [isOpen, onClose])

  const tableRows = useMemo(() => {
    if (!components?.length) return []
    const total = components.reduce((acc, c) => acc + c.line_cost, 0) || 1
    return components.map((c) => ({
      ...c,
      pct: (c.line_cost / total) * 100,
    }))
  }, [components])

  if (!isOpen || !item) return null

  // Tab-cycle within the panel. Pulled out of the effect so it runs
  // against the current DOM at the moment of the keypress (no stale
  // refs). Safe to define inline — only attached to the panel when
  // the modal is actually open (we early-return above).
  function handlePanelKeyDown(e) {
    if (e.key !== 'Tab') return
    const panel = panelRef.current
    if (!panel) return
    const focusables = panel.querySelectorAll(FOCUSABLE_SELECTOR)
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    // If focus is on the panel itself (e.g. user clicked the rationale
    // text or table body — both non-focusable but inside the panel,
    // which has tabIndex=-1) or has escaped the panel entirely, pull
    // it back into the focus ring. Without this, Tab would walk out
    // to the next focusable in the document and leave the modal.
    if (active === panel || !panel.contains(active)) {
      e.preventDefault()
      if (e.shiftKey) {
        last.focus?.()
      } else {
        first.focus?.()
      }
      return
    }
    if (e.shiftKey && active === first) {
      e.preventDefault()
      last.focus?.()
    } else if (!e.shiftKey && active === last) {
      e.preventDefault()
      first.focus?.()
    }
  }

  // Classification label tone — match MenuEngineeringCard palette
  const classification = item.classification || 'dog'
  const classTone = CLASS_TONES[classification] || CLASS_TONES.dog

  return (
    <div className="fixed inset-0 z-40">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
        className="animate-sheet-up absolute inset-x-0 bottom-0 z-10 max-h-[95vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-label={`Recipe breakdown for ${item.item_name}`}
        aria-describedby="menu-kill-detail-desc"
      >
        <span id="menu-kill-detail-desc" className="sr-only">
          Recipe component breakdown for {item.item_name}, classified as{' '}
          {classification}.
        </span>
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-start justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <h2 className="truncate text-xl font-bold">{item.item_name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${classTone.badge}`}>
                  {classification}
                </span>
                {marginPct != null && (
                  <span className="text-xs text-black/60 tabular-nums">
                    {marginPct}% margin
                  </span>
                )}
                {targetFoodCostPct != null && (
                  <span className="text-xs text-black/55 tabular-nums">
                    target food cost {targetFoodCostPct}%
                  </span>
                )}
              </div>
            </div>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              className="min-h-btn shrink-0 rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Close"
            >
              Close
            </button>
          </div>
        </div>

        <div className="space-y-4 px-4 pb-6">
          {loading && (
            <KitchenLoader
              messages={[
                'Reading the recipe…',
                'Pricing each component…',
                'Adding it up…',
              ]}
            />
          )}

          {error && !loading && (
            <div className="rounded-xl border border-red-200 bg-red-50/60 p-3 text-sm text-red-800">
              {error}
            </div>
          )}

          {!loading && !error && tableRows.length === 0 && item.item_id == null && (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              We couldn&rsquo;t find an item id for this row — link a recipe to see
              the cost breakdown.
            </div>
          )}

          {!loading && !error && tableRows.length === 0 && item.item_id != null && (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-6 text-center text-sm text-black/60">
              No recipe components are attached to this item. Add a recipe in{' '}
              <span className="font-semibold">Settings → Recipes</span> to see
              the cost breakdown.
            </div>
          )}

          {!loading && !error && tableRows.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-black/55">
                Recipe cost breakdown
              </div>
              <ul className="mt-2 divide-y divide-black/5 overflow-hidden rounded-xl border border-black/10">
                {tableRows.map((c) => (
                  <li key={c.id} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs">
                    <div className="col-span-5 truncate font-medium text-black/85">
                      {c.name}
                    </div>
                    <div className="col-span-2 tabular-nums text-right text-black/70">
                      {formatQty(c.qty, c.unit)}
                    </div>
                    <div className="col-span-2 tabular-nums text-right text-black/60">
                      {formatMoney(c.unit_cost)}
                    </div>
                    <div className="col-span-2 tabular-nums text-right font-semibold text-black/85">
                      {formatMoney(c.line_cost)}
                    </div>
                    <div className="col-span-1 tabular-nums text-right text-[10px] text-black/45">
                      {Math.round(c.pct)}%
                    </div>
                  </li>
                ))}
              </ul>

              <div className="mt-3 rounded-xl border border-black/10 bg-black/[0.02] px-3 py-2.5">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-black/65">Food cost / serving</span>
                  <span className="font-bold tabular-nums">{formatMoney(totalCost)}</span>
                </div>
                {salePrice > 0 && (
                  <div className="mt-1 flex items-baseline justify-between text-sm">
                    <span className="text-black/65">Sale price</span>
                    <span className="font-bold tabular-nums">{formatMoney(salePrice)}</span>
                  </div>
                )}
                {salePrice > 0 && (
                  <div className="mt-1 flex items-baseline justify-between text-sm">
                    <span className="text-black/65">Contribution margin</span>
                    <span className="font-bold tabular-nums text-emerald-700">
                      {formatMoney(salePrice - totalCost)}
                      {marginPct != null && (
                        <span className="ml-1 text-xs font-medium text-black/55">
                          ({marginPct}%)
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {briefRationale && (
            <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 text-sm leading-relaxed text-blue-900">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-blue-900/70">
                AI rationale
              </div>
              <div className="mt-1 italic">{briefRationale}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const CLASS_TONES = {
  star:      { badge: 'bg-emerald-100 text-emerald-900' },
  plowhorse: { badge: 'bg-amber-100 text-amber-900' },
  puzzle:    { badge: 'bg-blue-100 text-blue-900' },
  dog:       { badge: 'bg-red-100 text-red-900' },
}
